/**
 * Deep content for static hub pages (not in the EcosystemRoutePage system).
 *
 * Pages covered:
 *   - Men's Wellness
 *   - Women's Wellness
 *   - Relationships (overview)
 *   - Families (overview)
 *   - Teens & Youth
 *   - Wellness (overview)
 *   - Business (overview)
 */

export const STATIC_HUB_DEEP_CONTENT = {
  mens: {
    pageTitle: "Men's Wellness",
    askDonnyPrompt: "Help me build an honest, sustainable wellness routine as a busy man.",
    editorial: [
      {
        title: "Men's wellness is finally getting honest",
        body:
          "For decades, men's health was either gym-culture noise or quiet suffering. Both miss the point. Modern men's wellness is about energy, hormonal awareness, focus, recovery, and the unspoken cost of holding it all together.\n\nStart with sleep. Build from there.",
      },
      {
        title: "Andropause is real — and worth understanding",
        body:
          "Hormonal shifts happen to men, too. Energy dips, mood changes, libido shifts — these aren't \"just life.\" They're signals. Track them. Get tested. Address the lifestyle drivers first (sleep, stress, training, nutrition), then talk to a doctor if needed.",
      },
    ],
    quotes: [
      { text: "The strongest men are the gentlest.", author: "DONiTSH!T Editorial" },
      { text: "If you're not asking for help, you're carrying everything alone.", author: "Mens' wellness aphorism" },
      { text: "Strength without recovery is a slow injury.", author: "Performance science" },
    ],
    products: [
      { kind: "Program", title: "Vata Reset for Men", summary: "A 28-day program of grounding habits for high-energy, low-recovery men.", to: "/shop" },
      { kind: "eBook", title: "Andropause Honestly", summary: "What changes in midlife — and what to do about it.", to: "/shop" },
      { kind: "Course", title: "Calm Drive", summary: "Confidence, focus, and emotional fluency for modern men.", to: "/shop" },
    ],
  },

  womens: {
    pageTitle: "Women's Wellness",
    askDonnyPrompt: "Help me build a wellness routine that respects my cycle.",
    editorial: [
      {
        title: "Wellness that respects the cycle",
        body:
          "Your energy, mood, sleep, and metabolism shift across the cycle. Training, eating, and resting in alignment with those phases isn't a fad — it's biology.\n\nStart by tracking. The patterns will surprise you, and the freedom is immense.",
      },
      {
        title: "Perimenopause: the conversation we should have earlier",
        body:
          "Perimenopause begins years before menopause, often quietly. Sleep changes, mood shifts, cycle irregularity — these are signals worth understanding. Information empowers.",
      },
    ],
    quotes: [
      { text: "The cycle isn't a problem to solve. It's data to honor.", author: "DONiTSH!T Editorial" },
      { text: "Rest is productive. Pleasure is data. Self-knowledge is power.", author: "Women's wellness ethos" },
      { text: "You are allowed to outgrow your routine.", author: "Wellness reminder" },
    ],
    products: [
      { kind: "Program", title: "Cycle-Aligned 28", summary: "Movement, food, and rest synced to your phases.", to: "/shop" },
      { kind: "eBook", title: "Perimenopause, Explained", summary: "An honest guide to the years before menopause.", to: "/shop" },
      { kind: "Course", title: "Postpartum Foundations", summary: "Recovery and identity in the fourth trimester.", to: "/shop" },
    ],
  },

  relationships: {
    pageTitle: "Relationships",
    askDonnyPrompt: "How do I take a small step today to strengthen a relationship that matters?",
    editorial: [
      {
        title: "Relationships are wellness infrastructure",
        body:
          "The single biggest predictor of long-term wellbeing isn't your diet, your exercise routine, or your supplements — it's the quality of your relationships. Family, partner, friends, self.\n\nInvest accordingly.",
      },
      {
        title: "Connection is a daily practice",
        body:
          "Not grand gestures. Small acts. A real check-in instead of a passing \"how was your day.\" Eye contact at dinner. A text just to say you were thinking of them. Repair before bed. These compound.",
      },
    ],
    quotes: [
      { text: "Love is the only thing that grows when you give it away.", author: "Anon." },
      { text: "Repair is the most underrated relationship skill.", author: "DONiTSH!T Editorial" },
      { text: "Choose each other on purpose — every day.", author: "Long-term love" },
    ],
    products: [
      { kind: "Course", title: "Secure-Functioning Couples", summary: "Build the skills of safe, loving partnership.", to: "/shop" },
      { kind: "eBook", title: "The Repair Toolkit", summary: "Concrete scripts for hard moments.", to: "/shop" },
      { kind: "Program", title: "Connection Reset, 21 Days", summary: "A daily practice for re-energizing your relationships.", to: "/shop" },
    ],
  },

  families: {
    pageTitle: "Families & Parenting",
    askDonnyPrompt: "Help me design a simple weekly rhythm for our family.",
    editorial: [
      {
        title: "Families are wellness ecosystems",
        body:
          "A family is the first ecosystem any child ever knows. Healthy families aren't built on perfect parents — they're built on repair, rhythm, and presence.",
      },
      {
        title: "Small rituals, large dividends",
        body:
          "Tuesday-night dinners. Sunday-morning walks. A 2-minute check-in at bedtime. These are infrastructure for emotional safety, and they outlast any grand parenting strategy.",
      },
    ],
    quotes: [
      { text: "Connection before correction.", author: "Modern parenting principle" },
      { text: "Calm parents raise calm kids.", author: "Developmental psychology" },
      { text: "Family rhythms are the architecture of belonging.", author: "DONiTSH!T Editorial" },
    ],
    products: [
      { kind: "Program", title: "The Calm Parent, 30 Days", summary: "Tiny daily practices that change the tone of your home.", to: "/shop" },
      { kind: "eBook", title: "Discipline Without Shame", summary: "Setting limits with warmth.", to: "/shop" },
      { kind: "Course", title: "Family Rhythms That Last", summary: "Build the small practices that hold a family together.", to: "/shop" },
    ],
  },

  teens: {
    pageTitle: "Teens & Youth",
    askDonnyPrompt: "How do I support a teen who's struggling with anxiety and social media?",
    editorial: [
      {
        title: "Teens need us to stay calm",
        body:
          "Adolescence is loud. The most important thing a parent can offer is a steady nervous system. Stay calm. Stay curious. Stay open. The behavior often softens when the relationship stays sturdy.",
      },
      {
        title: "Social media is a wellness conversation",
        body:
          "Don't ban — discuss. Curate together. Set agreements about devices in bedrooms, at meals, and after a certain hour. Model what you'd like them to learn. Imperfectly is fine.",
      },
    ],
    quotes: [
      { text: "Stay calm. Stay curious. Stay close.", author: "Adolescent parenting" },
      { text: "Teens don't need perfect parents — they need present ones.", author: "DONiTSH!T Editorial" },
      { text: "Be the safe place they fight against and return to.", author: "Family wisdom" },
    ],
    products: [
      { kind: "Course", title: "Parenting Teens with Confidence", summary: "Practical skills for the adolescent years.", to: "/shop" },
      { kind: "eBook", title: "Tech-Healthy Teens", summary: "A guide for parents who want to lead the conversation.", to: "/shop" },
      { kind: "Program", title: "Teen Anxiety: A Parent's Companion", summary: "How to support without rescuing.", to: "/shop" },
    ],
  },
};
